// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Cairn — the three modes (principle #14). The RULE that ends a run is the axis, not just the
// cairn size: Ramble is one calm cairn with no clock and a generous scree — running the scree
// out just ends the round, it is never a loss; Cascade is a THROUGHPUT run — a stream of small
// cairns against a clock, bank as many as you can; Scree is one big cairn with a HARD draw
// budget — run out of draws with stones still standing and you are BURIED, the run is over.
// Those are structurally different games, not one game with a dial.
//
// The host's chosen mode travels FROZEN inside the round start (rematch roundOpts); a guest
// renders state().hostOpts. An id off the wire is always modeOf()-validated (Object.hasOwn, so a
// wire key can never resolve a prototype member) — unknown falls back to Ramble.

export type ModeId = 'ramble' | 'cascade' | 'scree';

export interface Mode {
  readonly id: ModeId;
  readonly name: string;
  readonly blurb: string;
  /** Pyramid height: rows 0..h-1, row r has r+1 stones → h(h+1)/2 stones. */
  readonly h: number;
  /** Cascade only: a run is a stream of fresh cairns. */
  readonly stream: boolean;
  /** Cascade only: run time limit in ms (0 = no clock). */
  readonly limitMs: number;
  /** Generator: probability a removal in the solution needs a fresh draw (tunes difficulty). */
  readonly drawProb: number;
  /** Draw budget granted beyond the solution's own draws (small = tight = plan-or-die). */
  readonly slack: number;
  /** Scree: running the draw budget out with stones left is a LOSS (buried). Ramble: a soft end. */
  readonly lethal: boolean;
}

export const MODES: Record<ModeId, Mode> = {
  ramble: {
    id: 'ramble',
    name: 'Ramble',
    blurb: 'One cairn, no clock, a generous scree. Chain a stone one rune above or below your hand — aces wrap to kings — and clear the whole mountain at your pace.',
    h: 6, // 21 stones
    stream: false,
    limitMs: 0,
    drawProb: 0.3,
    slack: 16,
    lethal: false,
  },
  cascade: {
    id: 'cascade',
    name: 'Cascade',
    blurb: 'A stream of small cairns against a 60-second clock. Clear one and the next deals instantly — bank as many as you can. A stuck cairn is swapped for a fresh one.',
    h: 4, // 10 stones
    stream: true,
    limitMs: 60_000,
    drawProb: 0.32,
    slack: 4,
    lethal: false,
  },
  scree: {
    id: 'scree',
    name: 'Scree',
    blurb: 'One big cairn and a hard draw budget. Every draw resets your heat and spends the budget — run out with stones still standing and you are buried. Plan each move.',
    h: 7, // 28 stones
    stream: false,
    limitMs: 0,
    drawProb: 0.22,
    slack: 4,
    lethal: true,
  },
};

export const MODE_IDS: ModeId[] = ['ramble', 'cascade', 'scree'];

export const DEFAULT_MODE: Mode = MODES.ramble;

/** Resolve a (possibly untrusted, off-the-wire) id to a Mode; unknown → Ramble. */
export function modeOf(id: unknown): Mode {
  if (typeof id === 'string' && Object.hasOwn(MODES, id)) {
    return MODES[id as ModeId];
  }
  return DEFAULT_MODE;
}
